/**
 * Configures the menu layout.
 */
import type { RouteConfig } from "vue-router";
import type {
  Location,
  Route,
  RoutePropsFunction,
} from "vue-router/types/router";
import type { VueConstructor } from "vue";
import type { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import type { ComponentPublicInstanceConstructor } from "vue/types/v3-component-public-instance";
import type { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import Key from "@/app/types/common/key";
import { translate } from "@/plugins/i18n";

export interface RouteMetadata {
  preSession?: boolean;
  helpUrl?: string;
  target?: string;

  // Key on which to refresh the entire main router-view component, extract from route. Uses route.fullUrl if not specified
  // Can be used to prevent full refresh on Eg tab navigation if a page contains sub-routes.
  routeKey?: (route: Route) => string;
}

export interface IRouteItem {
  name?: string;
  // TODO - make url mandatory for routes once they are properly split from menus.
  url?: string;
  view?: () => Promise<any>;
  routeMetadata: RouteMetadata;
  childRoutes: RouteConfig[];
  componentProps?: boolean | Record<string, any> | RoutePropsFunction;
  requiredUserPermissions?: EnumUserPermission[];

  // TODO - Not sure what functionality this gives, should try to remove it.
  redirect?: string;

  // TODO - This should not be here. It is included as a small step of migrating from
  // the old menus, but should be flattened out when the available routes are evaluated
  // not included within a route.
  subMenus?: IRouteItem[];
}

export interface IRouteWithLocation<TParams extends any[]> extends IRouteItem {
  location: (...params: TParams) => Location;
}

interface RequiredMenuLeafOptions {
  url: string;
  view: () => Promise<any>;
}

interface MenuOptions {
  name: string;
  icon: string;
  routeMetadata: RouteMetadata;
  requiredFeatureFlag: EnumFeatureFlag;
  visible: boolean | (() => boolean);
  childRoutes: RouteConfig[];
  breadcrumbParentName: string;
  componentProps?: boolean | Record<string, any> | RoutePropsFunction;
  menuComponent?: VueConstructor | ComponentPublicInstanceConstructor;
  // This is rather confusing. Code used to accept translationKey as a string, and it was generally
  // passed as a string but then looked up as a translation, which was very not type-safe on the translations
  // lookups. Hoewever, it is also used for other things, such as Vue id tags and data-qa tags so it is
  // very difficult to remove.
  // Taking small steps I have decided to introduce "label", which is typed as a string or Key (string being a literal)
  // so it is hopefully clearer. If label is not provided it will get populated based on the translation key
  // for backwards compatibility.
  translationKey: string;
  label: Key | string;
  requiredUserPermissions?: EnumUserPermission[];
  redirect?: string;
}

export class MenuItem {
  public isActive: boolean = false;
  public translationKey?: string;
  private label: Key | string;

  public constructor(
    public name: string | undefined,
    public icon: string,
    public url?: string,
    public subMenus?: MenuItem[],
    public view?: () => Promise<any>,
    public routeMetadata: RouteMetadata = {},
    public requiredFeatureFlag?: EnumFeatureFlag,
    public visible: boolean | (() => boolean) = true,
    public childRoutes: RouteConfig[] = [],
    public breadcrumbParentName?: string,
    public componentProps?: boolean | Record<string, any> | RoutePropsFunction,
    public menuComponent?: VueConstructor | ComponentPublicInstanceConstructor,
    translationKey?: string,
    label?: Key | string,
    public requiredUserPermissions?: EnumUserPermission[],
    public redirect?: string
  ) {
    this.translationKey =
      translationKey ?? (label instanceof Key ? label.value : label);
    this.label =
      label ?? (translationKey ? Key.fromString(translationKey) : "");
    if (routeMetadata.routeKey) {
      for (const child of childRoutes.flatMap((x) =>
        (x.children ?? []).concat(x)
      )) {
        child.meta ??= {};
        child.meta.routeKey ??= routeMetadata.routeKey;
      }
    }
  }

  public static fromOpts(opts: RequiredMenuLeafOptions & Partial<MenuOptions>) {
    return new MenuItem(
      opts.name,
      opts.icon || "",
      opts.url,
      undefined,
      opts.view,
      opts.routeMetadata,
      opts.requiredFeatureFlag,
      opts.visible,
      opts.childRoutes,
      opts.breadcrumbParentName,
      opts.componentProps,
      opts.menuComponent,
      opts.translationKey || opts.name,
      opts.label,
      opts.requiredUserPermissions,
      opts.redirect
    );
  }

  public displayText() {
    return this.label instanceof Key ? translate(this.label) : this.label;
  }

  public static forBranch(opts: {
    name?: string;
    icon?: string;
    subMenus: MenuItem[];
    requiredFeatureFlag?: EnumFeatureFlag;
    visible?: boolean | (() => boolean);
    menuComponent?: VueConstructor | ComponentPublicInstanceConstructor;
    translationKey?: string;
    requiredUserPermissions?: EnumUserPermission[];
  }) {
    return new MenuItem(
      opts.name,
      opts.icon || "",
      undefined,
      opts.subMenus,
      undefined,
      undefined,
      opts.requiredFeatureFlag,
      opts.visible,
      undefined,
      undefined,
      undefined,
      opts.menuComponent,
      opts.translationKey || opts.name,
      undefined,
      opts.requiredUserPermissions,
      undefined
    );
  }

  public static forRoute(
    label: Key | string,
    route: IRouteItem,
    opts?: {
      icon?: string;
      requiredFeatureFlag?: EnumFeatureFlag;
      visible?: boolean | (() => boolean);
      menuComponent?: VueConstructor | ComponentPublicInstanceConstructor;
      requiredUserPermissions?: EnumUserPermission[];
    }
  ) {
    return new MenuItem(
      route.name,
      opts?.icon || "",
      route.url,
      undefined,
      route.view,
      route.routeMetadata,
      opts?.requiredFeatureFlag,
      opts?.visible,
      undefined,
      undefined,
      route.componentProps,
      opts?.menuComponent,
      undefined,
      label,
      opts?.requiredUserPermissions,
      undefined
    );
  }
}
